import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type {
  OperationsNode,
  OperationsScheduleNode,
} from '@sportninja/common/features/competition/operations/types/operations.types'
import AddTeamModal from './addTeam/AddTeamModal'
import EditTeamModal from './EditTeamModal'
import DivisionModal from './DivisionModal'
import type { PendingCreate } from '../types/pendingCreate'

interface EditEntityLauncherProps {
  /** The node being edited, or null when no modal is open. */
  node: OperationsNode | null
  /** A create-in-progress, checked before `node`. */
  pendingCreate?: PendingCreate | null
  orgId: string
  /** Root competition — the tree query key the create invalidates. */
  rootScheduleId: string
  /** Canlan-only registration/Salesforce fields on the division form. */
  showSalesforceSettings?: boolean
  /** Sport of the competition — narrows the add-team search. */
  sportId?: string | null
  /** Generic-sport competitions narrow the same search by this instead. */
  genericSportId?: string | null
  /** Edit only: the node's parent, for the form's type-option filtering. */
  editParent?: OperationsScheduleNode | null
  onClose: () => void
  onSaved: () => void
}

/**
 * Maps a selected node to the right dialog.
 *
 * Adding a team uses this feature's own `AddTeamModal` (search an existing team
 * or name a new one, then pick its roster). Editing one uses the shared
 * `TeamModal`. A season/conference/division uses this
 * feature's own `DivisionModal` for BOTH create and edit — the same pairing the
 * legacy Setup panel had, where one form served either mode. The full
 * `ScheduleModal` competition wizard is not used from this tab.
 */
const EditEntityLauncher = ({
  node,
  pendingCreate,
  orgId,
  rootScheduleId,
  showSalesforceSettings,
  sportId = null,
  genericSportId = null,
  editParent,
  onClose,
  onSaved,
}: EditEntityLauncherProps) => {
  'use memo'

  const handleSaved = () => {
    onSaved()
    onClose()
  }

  if (pendingCreate) {
    if (pendingCreate.kind === 'team') {
      // Search-or-create plus the roster step, matching the legacy Setup tab.
      // `TeamModal` is create-only and is still used for EDITING a team below.
      return (
        <AddTeamModal
          division={pendingCreate.parent}
          orgId={orgId}
          rootScheduleId={rootScheduleId}
          sportId={sportId}
          genericSportId={genericSportId}
          onClose={onClose}
          onAdded={handleSaved}
        />
      )
    }

    // A child competition uses the compact division form, NOT the full
    // ScheduleModal wizard — same form and same POST as the legacy Setup tab.
    return (
      <DivisionModal
        parent={pendingCreate.parent}
        orgId={orgId}
        rootScheduleId={rootScheduleId}
        showSalesforceSettings={showSalesforceSettings}
        onClose={onClose}
        onCreated={handleSaved}
      />
    )
  }

  if (!node) {
    return null
  }

  if (isTeamNode(node)) {
    // Loads the full team before mounting the modal — a tree node carries far
    // less than the edit form writes back. See `EditTeamModal`.
    return (
      <EditTeamModal
        teamId={node.teamId}
        orgId={orgId}
        onClose={onClose}
        onSaved={handleSaved}
      />
    )
  }

  // Editing a season/conference/division uses the SAME dialog as creating one,
  // matching the legacy Setup panel — not the full ScheduleModal wizard.
  return (
    <DivisionModal
      // On edit the payload comes from `node`; `parent` is only consulted for
      // the create path, so passing the node itself is safe and keeps the prop
      // required.
      parent={editParent ?? node}
      node={node}
      isRoot={node.scheduleId === rootScheduleId}
      orgId={orgId}
      rootScheduleId={rootScheduleId}
      showSalesforceSettings={showSalesforceSettings}
      onClose={onClose}
      onCreated={handleSaved}
    />
  )
}

export default EditEntityLauncher
